// Three.js プレビュー。メッシュ + モーフターゲット描画、タッチ対応オービットカメラ。
// M3: liltoon 近似 ShaderMaterial（環境光 + ディレクショナル 1 灯 + アウトライン）。
// ライティングはシェーダ内 uniform で行うため、シーンに THREE のライトは置かない。

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type { LoadedAvatar } from "../core/types.ts";
import { shapeKeyOf } from "../core/types.ts";
import {
  buildGeometry,
  buildShapeDeltaTextures,
  createInfluenceTexture,
  type ShapeDeltaTextures,
} from "./geometry.ts";
import { buildMaterials, type BuiltMaterials } from "./materials.ts";
import {
  applyLighting,
  cloneWithShape,
  DEFAULT_LIGHTING,
  type LightingSettings,
  type ShapeBinding,
} from "./liltoon.ts";
import { flipZQuaternion } from "./convert.ts";
import { el } from "../ui/dom.ts";

/** プレビュー背景色（スマホでの見やすさ確認用） */
export const BACKGROUNDS = {
  dark: 0x1b1e24,
  light: 0xf2f4f8,
  green: 0x00b140,
} as const;
export type BackgroundKey = keyof typeof BACKGROUNDS;

/** アウトラインを持たないサブメッシュ用のダミー（描画しない） */
const INVISIBLE = new THREE.MeshBasicMaterial({ visible: false });

interface MeshEntry {
  mesh: THREE.Mesh;
  /** アウトライン（反転ハル）用の重ね描きメッシュ。無い場合は null */
  outline: THREE.Mesh | null;
  /** シェイプキー名 → シェイプインデックス */
  shapeIndex: Map<string, number>;
  meshIndex: number;
  /** シェイプデルタテクスチャ（シェイプキー 0 個なら null） */
  shapes: ShapeDeltaTextures | null;
  /** 影響度 CPU 配列（本体・アウトラインで共有するテクスチャの中身） */
  shapeInfluences: Float32Array;
  /** 影響度テクスチャ。本体・アウトライン双方の uniform が同一インスタンスを指す */
  shapeInfluencesTex: THREE.DataTexture | null;
  /** このメッシュ専用にクローンしたマテリアル（dispose 対象） */
  ownedMaterials: THREE.Material[];
}

export class AvatarViewer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly controls: OrbitControls;
  private readonly root: THREE.Group;
  private readonly container: HTMLElement;
  private readonly resizeObserver: ResizeObserver;

  private entries: MeshEntry[] = [];
  private materials: BuiltMaterials | null = null;
  private frameHandle = 0;
  private disposed = false;
  private bounds = new THREE.Box3();
  private background: BackgroundKey = "dark";
  private lighting: LightingSettings = { ...DEFAULT_LIGHTING };

  constructor(container: HTMLElement) {
    this.container = container;
    if (import.meta.env.DEV) (window as unknown as Record<string, unknown>).__fmViewer = this;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.domElement.style.display = "block";
    this.renderer.domElement.style.width = "100%";
    this.renderer.domElement.style.height = "100%";
    this.renderer.domElement.style.touchAction = "none";
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1b1e24);

    this.camera = new THREE.PerspectiveCamera(30, 1, 0.01, 100);
    this.camera.position.set(0, 1.5, 0.6);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    this.controls.rotateSpeed = 0.7;
    this.controls.panSpeed = 0.7;
    this.controls.minDistance = 0.05;
    this.controls.maxDistance = 5;
    this.controls.screenSpacePanning = true;
    // タッチ: 1 本指で回転、2 本指でズーム + パン
    this.controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };

    this.root = new THREE.Group();
    this.scene.add(this.root);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();
    this.startLoop();
  }

  /** 背景色を切り替える */
  setBackground(key: BackgroundKey): void {
    this.background = key;
    (this.scene.background as THREE.Color).setHex(BACKGROUNDS[key] ?? BACKGROUNDS.dark);
  }

  getBackground(): BackgroundKey {
    return this.background;
  }

  /** ライト方向・強さを変更する（全 liltoon マテリアルで uniform 共有） */
  setLighting(s: LightingSettings): void {
    this.lighting = { ...s };
    if (this.materials) applyLighting(this.materials.lighting, this.lighting);
  }

  getLighting(): LightingSettings {
    return { ...this.lighting };
  }

  /** アバターを読み込んでシーンを差し替える */
  setAvatar(avatar: LoadedAvatar): void {
    this.clearAvatar();
    this.materials = buildMaterials(avatar);
    applyLighting(this.materials.lighting, this.lighting);

    for (const info of avatar.face.meshes) {
      const block = avatar.meshes.blocks.find((b) => b.meshIndex === info.index);
      if (!block) continue;

      const geo = buildGeometry(block, info);

      // シェイプキーは Three 標準の morphAttributes ではなく自前のデルタテクスチャで扱う
      const maxTex = this.renderer.capabilities.maxTextureSize;
      let shapes: ShapeDeltaTextures | null = null;
      try {
        shapes = buildShapeDeltaTextures(block, maxTex);
      } catch (e) {
        this.shapeErrors.push(e instanceof Error ? e.message : String(e));
        shapes = null;
      }
      const infl = shapes ? createInfluenceTexture(shapes.count) : null;
      const binding: ShapeBinding | null =
        shapes && infl
          ? {
              posTex: shapes.posTex,
              nrmTex: shapes.nrmTex,
              influencesTex: infl.texture,
              count: shapes.count,
              texWidth: shapes.texWidth,
              rowsPerShape: shapes.rowsPerShape,
            }
          : null;

      // シェイプ uniform はメッシュ固有なのでテンプレートを clone して割り当てる。
      // 同一メッシュ内で同じテンプレートを使うサブメッシュはクローンを使い回す。
      const ownedMaterials: THREE.Material[] = [];
      const cloneCache = new Map<THREE.Material, THREE.Material>();
      const bind = (tmpl: THREE.Material): THREE.Material => {
        const hit = cloneCache.get(tmpl);
        if (hit) return hit;
        const m = cloneWithShape(tmpl, binding, this.materials!.lighting);
        cloneCache.set(tmpl, m);
        if (m !== tmpl) ownedMaterials.push(m);
        return m;
      };

      const mats: THREE.Material[] = [];
      const outlineMats: (THREE.Material | null)[] = [];
      for (let s = 0; s < block.submeshCount; s++) {
        const matIdx = info.submeshMaterials[s] ?? info.submeshMaterials[0] ?? -1;
        const m = this.materials.byIndex.get(matIdx);
        if (m) {
          mats.push(bind(m));
        } else {
          const fb = new THREE.MeshBasicMaterial({ color: 0xbbbbbb });
          ownedMaterials.push(fb);
          mats.push(fb);
        }
        const om = this.materials.outlineByIndex.get(matIdx);
        outlineMats.push(om ? bind(om) : null);
      }

      const mesh = new THREE.Mesh(geo, mats.length === 1 ? mats[0] : mats);
      mesh.name = info.name;
      mesh.position.set(block.transform.position[0], block.transform.position[1], -block.transform.position[2]);
      const q = flipZQuaternion(block.transform.rotation);
      mesh.quaternion.set(q[0], q[1], q[2], q[3]);
      mesh.frustumCulled = false;

      const shapeIndex = new Map<string, number>();
      info.blendshapeNames.forEach((n, i) => shapeIndex.set(n, i));

      this.root.add(mesh);

      // アウトライン: 同一ジオメトリを BackSide で重ね描き。
      // アウトラインを持たないサブメッシュは描画しない透明マテリアルで潰す。
      let outline: THREE.Mesh | null = null;
      if (outlineMats.some((m) => m !== null)) {
        const filled = outlineMats.map((m) => m ?? INVISIBLE);
        outline = new THREE.Mesh(geo, filled.length === 1 ? filled[0] : filled);
        outline.name = `${info.name} (outline)`;
        outline.position.copy(mesh.position);
        outline.quaternion.copy(mesh.quaternion);
        outline.frustumCulled = false;
        outline.renderOrder = -1;
        this.root.add(outline);
      }

      this.entries.push({
        mesh,
        outline,
        shapeIndex,
        meshIndex: info.index,
        shapes,
        shapeInfluences: infl?.data ?? new Float32Array(0),
        shapeInfluencesTex: infl?.texture ?? null,
        ownedMaterials,
      });
    }

    // setFromObject はモーフデルタまで含めてボックスを膨らませるため、
    // ベース頂点の position 属性だけからバウンズを計算する
    this.bounds = new THREE.Box3();
    const tmp = new THREE.Vector3();
    for (const { mesh } of this.entries) {
      const pos = (mesh.geometry as THREE.BufferGeometry).getAttribute("position");
      mesh.updateWorldMatrix(true, false);
      for (let i = 0; i < pos.count; i++) {
        tmp.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
        this.bounds.expandByPoint(tmp);
      }
    }
    // まばたきシェイプのデルタ頂点の重心 ≒ 目の位置。ケモミミ等で
    // バウンズ上端が頭より高いアバターでも正しく顔を狙えるようにする
    this.eyeTarget = this.computeEyeTarget(avatar);
    this.frameFace();
  }

  private eyeTarget: THREE.Vector3 | null = null;

  private computeEyeTarget(avatar: LoadedAvatar): THREE.Vector3 | null {
    const sp = avatar.face.specialShapes;
    const names = [sp.blink.combined, sp.blink.left, sp.blink.right].filter(
      (n): n is string => !!n
    );
    if (names.length === 0) return null;
    const sum = new THREE.Vector3();
    let count = 0;
    const tmp = new THREE.Vector3();
    for (const info of avatar.face.meshes) {
      const shapeIdx = names
        .map((n) => info.blendshapeNames.indexOf(n))
        .filter((i) => i >= 0);
      if (shapeIdx.length === 0) continue;
      const block = avatar.meshes.blocks.find((b) => b.meshIndex === info.index);
      const entry = this.entries.find((e) => e.meshIndex === info.index);
      if (!block || !entry) continue;
      const pos = entry.mesh.geometry.getAttribute("position");
      entry.mesh.updateWorldMatrix(true, false);
      for (const si of shapeIdx) {
        const bs = block.blendshapes[si];
        if (!bs) continue;
        for (let k = 0; k < bs.indices.length; k++) {
          tmp.fromBufferAttribute(pos, bs.indices[k]).applyMatrix4(entry.mesh.matrixWorld);
          sum.add(tmp);
          count++;
        }
      }
    }
    return count > 0 ? sum.divideScalar(count) : null;
  }

  /** 顔（まばたきシェイプの位置、なければバウンズ上部推定）を中心にカメラを合わせる */
  frameFace(): void {
    if (this.bounds.isEmpty()) return;
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    this.bounds.getSize(size);
    this.bounds.getCenter(center);

    // 顔の位置と大きさの推定。
    // 全身メッシュ（高さ 0.5m 超）なら頭は上端付近・頭部高は全体の約 1/7。
    // 顔だけを書き出したメッシュならメッシュ全体がほぼ顔なので、そのまま中心を使う。
    const isFullBody = size.y > 0.5;
    let headHeight = isFullBody ? size.y / 7 : size.y;
    let target: THREE.Vector3;
    if (this.eyeTarget) {
      // 目の位置が分かる場合はそこを狙う（少し下=顔中心へオフセット）
      if (isFullBody) headHeight = Math.min(headHeight, (this.bounds.max.y - this.eyeTarget.y) * 2.2);
      target = new THREE.Vector3(this.eyeTarget.x, this.eyeTarget.y - headHeight * 0.15, this.eyeTarget.z);
    } else {
      target = isFullBody
        ? new THREE.Vector3(center.x, this.bounds.max.y - headHeight * 0.55, center.z)
        : center.clone();
    }

    const faceSize = Math.max(headHeight * 1.3, 0.08);
    const dist = faceSize / (2 * Math.tan((this.camera.fov * Math.PI) / 360));

    this.controls.target.copy(target);
    this.camera.position.set(target.x, target.y, target.z - dist); // Z反転後アバターは-Z向きのため正面は-Z側
    this.camera.near = Math.max(dist / 100, 0.005);
    this.camera.far = dist * 50;
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  /** メッシュ全体が入るようにカメラを引く */
  frameAll(): void {
    if (this.bounds.isEmpty()) return;
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    this.bounds.getSize(size);
    this.bounds.getCenter(center);
    const maxDim = Math.max(size.x, size.y, size.z, 0.1);
    const dist = maxDim / (2 * Math.tan((this.camera.fov * Math.PI) / 360)) * 1.4;
    this.controls.target.copy(center);
    this.camera.position.set(center.x, center.y, center.z - dist);
    this.camera.near = Math.max(dist / 100, 0.005);
    this.camera.far = dist * 50;
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  /**
   * シェイプキーの値を反映する。
   * @param values shapeKeyOf(meshIndex, name) → -1000〜1000（通常 0〜100）
   */
  applyShapeValues(values: ReadonlyMap<string, number>): void {
    for (const entry of this.entries) {
      const tex = entry.shapeInfluencesTex;
      if (!tex) continue;
      const infl = entry.shapeInfluences;
      for (const [name, i] of entry.shapeIndex) {
        if (i >= infl.length) continue;
        const v = values.get(shapeKeyOf(entry.meshIndex, name));
        // SPEC_FMAPPLY: 値は -1000〜1000（負値・100 超も許容）。影響度テクスチャは Float で、
        // シェーダは delta * inf を線形加算するだけなので負値・1 超もそのまま効く
        infl[i] = v === undefined || !Number.isFinite(v) ? 0 : Math.max(-1000, Math.min(1000, v)) / 100;
      }
      // 影響度テクスチャは本体・アウトラインで共有しているため同期処理は不要
      tex.needsUpdate = true;
    }
  }

  /** すべてのシェイプキーを 0 に戻す */
  resetShapes(): void {
    for (const entry of this.entries) {
      if (!entry.shapeInfluencesTex) continue;
      entry.shapeInfluences.fill(0);
      entry.shapeInfluencesTex.needsUpdate = true;
    }
  }

  /** setAvatar 時に発生したシェイプデルタテクスチャのエラー（端末上限超過など） */
  getShapeErrors(): string[] {
    return [...this.shapeErrors];
  }

  private shapeErrors: string[] = [];

  private clearAvatar(): void {
    for (const e of this.entries) {
      this.root.remove(e.mesh);
      if (e.outline) this.root.remove(e.outline);
      e.mesh.geometry.dispose();
      e.shapes?.dispose();
      e.shapeInfluencesTex?.dispose();
      for (const m of e.ownedMaterials) m.dispose();
    }
    this.entries = [];
    this.shapeErrors = [];
    this.materials?.dispose();
    this.materials = null;
  }

  private resize(): void {
    const w = this.container.clientWidth || 1;
    const h = this.container.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private startLoop(): void {
    const tick = (): void => {
      if (this.disposed) return;
      this.frameHandle = requestAnimationFrame(tick);
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    };
    this.frameHandle = requestAnimationFrame(tick);
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.frameHandle);
    this.resizeObserver.disconnect();
    this.clearAvatar();
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  /**
   * DOM に表示せず、オフスクリーンで一度だけ描画してサムネイル用の PNG dataURL を返す。
   * 失敗した場合は null を返す（呼び出し側は致命的に扱わないこと）。
   */
  static async captureThumbnail(avatar: LoadedAvatar, size = 128): Promise<string | null> {
    const container = el("div", {
      style: { position: "fixed", left: "-9999px", top: "-9999px", width: `${size}px`, height: `${size}px` },
    });
    document.body.appendChild(container);
    let viewer: AvatarViewer | null = null;
    try {
      viewer = new AvatarViewer(container);
      viewer.setAvatar(avatar);
      // テクスチャは Blob URL 経由で非同期デコードされるため、
      // 待たずに描画すると本体色が載る前の真っ黒な状態を撮ってしまう
      await viewer.materials?.ready;
      viewer.resize();
      viewer.controls.update();
      viewer.renderer.render(viewer.scene, viewer.camera);
      return viewer.renderer.domElement.toDataURL("image/png");
    } catch {
      return null;
    } finally {
      // 使い捨てレンダラの WebGL コンテキストは dispose だけでは即解放されず、
      // 繰り返すと上限（16 程度）に達して古いコンテキストが失われるため明示的に破棄する
      if (viewer) {
        const r = viewer.renderer;
        viewer.dispose();
        r.forceContextLoss();
      }
      container.remove();
    }
  }
}
